import { useEffect, useState } from "react";
import type { AxiosInstance } from "axios";
import { Alert, Button, Card, Form, Input, Space, Tag } from "@pankod/refine-antd";

type Settings =
  | { allowed: false }
  | { allowed: true; model: string; configured: boolean };

export function OpenRouterSettings({ api, apiUrl }: { api: AxiosInstance; apiUrl: string }) {
  const [settings, setSettings] = useState<Settings>();
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: "success" | "error"; text: string }>();
  const endpoint = `${apiUrl}/ops-assistant/settings`;

  useEffect(() => {
    let active = true;
    api.get<Settings>(endpoint).then(({ data }) => {
      if (!active) return;
      setSettings(data);
      if (data.allowed) setModel(data.model);
    }).catch(() => {
      if (active) setNotice({ type: "error", text: "Could not load Ops Assistant settings." });
    });
    return () => { active = false; };
  }, [api, endpoint]);

  async function save() {
    setNotice(undefined);
    setBusy(true);
    try {
      const { data } = await api.put<Extract<Settings, { allowed: true }>>(endpoint, {
        model: model.trim(), ...(apiKey.trim() && { apiKey: apiKey.trim() }),
      });
      setSettings({ ...data, allowed: true });
      setApiKey("");
      setNotice({ type: "success", text: "OpenRouter settings saved." });
    } catch {
      setNotice({ type: "error", text: "Could not save settings. Check the model ID and key format." });
    } finally {
      setBusy(false);
    }
  }

  return <Card title="Ops Assistant · OpenRouter" style={{ marginBottom: 24 }}>
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {notice && <Alert type={notice.type} message={notice.text} showIcon />}
      {settings?.allowed === false && <Alert type="info" showIcon
        message="Ops Assistant access is restricted"
        description="Ops Assistant requires a CMS Editor account." />}
      {settings?.allowed && <>
        <div><Tag color={settings.configured ? "green" : "default"}>{settings.configured ? "Key configured" : "Key required"}</Tag>
          The assistant uses this model for free-text requests.</div>
        <Form layout="vertical" onFinish={save} style={{ maxWidth: 540 }}>
          <Form.Item label="OpenRouter model ID" htmlFor="ops-openrouter-model" required>
            <Input id="ops-openrouter-model" value={model} maxLength={150} required disabled={busy}
              onChange={(event) => setModel(event.target.value)} />
          </Form.Item>
          <Form.Item label="OpenRouter API key" htmlFor="ops-openrouter-key"
            extra={settings.configured ? "Leave blank to keep the saved key. Enter a replacement to rotate it." : "The CMS saves and uses this key for OpenRouter requests."}>
            <Input.Password id="ops-openrouter-key" value={apiKey} maxLength={140} autoComplete="new-password"
              visibilityToggle={false} placeholder={settings.configured ? "Enter a replacement key" : "Enter an OpenRouter key"}
              onChange={(event) => setApiKey(event.target.value)} disabled={busy} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={busy} disabled={!model.trim()}>Save OpenRouter settings</Button>
        </Form>
      </>}
    </Space>
  </Card>;
}
